// kalanjay/pages/KalanjayBatches.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddIcon from "@mui/icons-material/Add";
import * as yup from "yup";
import AppTable from "../../../common/app-table";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";
import CustomTabs from "../../../common/custom-tabs";
import typography from "../../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
  inputColor,
} from "../../../cssUtils/constants";
import axios from "axios";
import {
  CREATE_KALANJAY_BATCH,
  DELETE_KALANJAY_BATCH,
  GET_KALANJAY_BATCHES,
  GET_KALANJAY_CENTRES,
  UPDATE_KALANJAY_BATCH,
} from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";
import NoPermission from "../../../common/NoPermission";

// ── shared field style ────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 40,
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.inputFieldGrey },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
  "& .MuiInputLabel-root": { ...typography.bodyLarge },
};

const BATCH_NAME_REGEX = /^[A-Za-z0-9 _-]+$/;

const batchSchema = yup.object().shape({
  name: yup
    .string()
    .required("Batch name is required")
    .matches(
      /^[A-Za-z0-9 _-]+$/,
      "Only letters, numbers, spaces, hyphens, and underscores allowed",
    ),
  centre: yup.mixed().required("Centre is required"),

  capacity: yup
    .number()
    .typeError("Capacity must be a number")
    .integer("Must be a whole number")
    .positive("Must be greater than 0")
    .required("Capacity is required"),
});

// ── Batch Add / Edit Dialog ───────────────────────────────────────────────────
const BatchFormDialog = ({ open, onClose, onSave, editData, centres }) => {
  const blank = { name: "", centre: "", capacity: "" };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});

  const validateForm = async () => {
    try {
      await batchSchema.validate(form, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const errObj = {};
      err.inner.forEach((e) => {
        errObj[e.path] = e.message;
      });
      setErrors(errObj);
      return false;
    }
  };

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name,
        centre: editData.centre, // ID
        capacity: String(editData.capacity),
      });
    } else if (centres.length > 0) {
      setForm({
        name: "",
        centre: centres[0].id,
        capacity: "",
      });
    }
  }, [open, editData, centres]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { m:2 } }}>
      <DialogTitle>{editData ? "Edit Batch" : "Add New Batch"}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Monday 5-6"
              error={!!errors.name}
              helperText={errors.name || "Use format: Monday 5-6"}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small" error={!!errors.centre}>
              <Select value={form.centre} onChange={set("centre")}>
                {centres.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>

              {errors.centre && (
                <Typography color="error" variant="caption">
                  {errors.centre}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.capacity}
              onChange={set("capacity")}
              placeholder="20"
              error={!!errors.capacity}
              helperText={errors.capacity}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <ButtonTwo onClick={onClose}>Cancel</ButtonTwo>
        <ButtonOne
          onClick={async () => {
            const isValid = await validateForm();
            if (!isValid) return;

            await onSave(form);
            onClose();
          }}
        >
          {" "}
          {editData ? "Save Changes" : "Add Batch"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

// ── Action icon button ────────────────────────────────────────────────────────
const ActionBtn = ({ icon: Icon, color, onClick, title }) => (
  <IconButton
    size="small"
    title={title}
    onClick={onClick}
    sx={{
      color: color || grayFontColor,
      "&:hover": { backgroundColor: ColorsConstants.primaryBlue60 },
    }}
  >
    <Icon fontSize="small" />
  </IconButton>
);

const KalanjayBatches = ({ fees = [], onSelectBatch }) => {
  const permissions = localStorage.getItem("permissions") || [];
  const hasViewPermission = permissions.includes("batches.view");
  const hasCreatePermission = permissions.includes("batches.create");
  const hasEditPermission = permissions.includes("batches.edit");

  const [centres, setCentres] = useState([]);
  const [batches, setBatches] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [centreTab, setCentreTab] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [errors, setErrors] = useState({});
  const { getValidToken } = useAuth();

  useEffect(() => {
    fetchCentres();
  }, []);

  useEffect(() => {
    if (centres.length > 0 && !centreTab) {
      const firstCentreId = centres[0].id;
      setCentreTab(firstCentreId);
      setPage(0);
      fetchBatches(rowsPerPage, 0, search, firstCentreId);
    }
  }, [centres, centreTab, search, rowsPerPage]);

  const fetchBatches = async (
    limit = 20,
    offset = 0,
    search = "",
    centreId = "",
  ) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const params = new URLSearchParams({ limit, offset });
      if (search) params.append("search", search);
      if (centreId) params.append("centre", centreId);

      const res = await axios.get(
        `${GET_KALANJAY_BATCHES}?${params.toString()}`,
        { headers },
      );
      const batchesFromApi = res?.data?.results || res.data;
      const count = res?.data?.count ?? batchesFromApi.length;
      setBatches(batchesFromApi);
      setTotalCount(count);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCentres = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await axios.get(GET_KALANJAY_CENTRES, {
        headers: headers,
      });

      const centresFromApi = response?.data?.results;
      setCentres(centresFromApi);
    } catch (error) {
      console.log(error);
    }
  };

  const onPageChange = (_, newPage) => {
    setPage(newPage);
    fetchBatches(rowsPerPage, newPage * rowsPerPage, search, centreTab);
  };

  const onRowsPerPageChange = (e) => {
    const val = Number(e?.target?.value);
    if (!val || isNaN(val)) return;
    setRowsPerPage(val);
    setPage(0);
    fetchBatches(val, 0, search, centreTab);
  };

  const onSearchChange = (val) => {
    setSearch(val);
    setPage(0);
    fetchBatches(rowsPerPage, 0, val, centreTab);
  };

  const onCentreChange = (centreId) => {
    setPage(0);
    fetchBatches(rowsPerPage, 0, search, centreId);
  };

  const validate = (form) => {
    const next = {};

    // name
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      next.name = "Batch name is required.";
    } else if (trimmedName.length > 50) {
      next.name = "Batch name must be 50 characters or fewer.";
    } else if (!BATCH_NAME_REGEX.test(trimmedName)) {
      next.name = "Only letters, numbers, and spaces are allowed.";
    }

    // centre
    if (!form.centre) {
      next.centre = "Centre is required.";
    }

    // capacity
    const capStr = String(form.capacity).trim();
    if (!capStr) {
      next.capacity = "Capacity is required.";
    } else if (!/^\d+$/.test(capStr)) {
      next.capacity =
        "Capacity must be a whole number (no decimals or letters).";
    } else if (parseInt(capStr, 10) <= 0) {
      next.capacity = "Capacity must be greater than 0.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const addBatch = async (formData) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      if (!formData.name || !formData.centre || !formData.capacity) {
        throw new Error("All fields are required");
      }

      const payload = {
        name: formData.name,
        centre: formData.centre,
        capacity: Number(formData.capacity),
      };

      const res = await axios.post(CREATE_KALANJAY_BATCH, payload, { headers });
      
      // Reload Batches
      setPage(0);
      fetchBatches(rowsPerPage, 0, search, centreTab);
    } catch (err) {
      console.error(err);
    }
  };

  const updateBatch = async (batchId, formData) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const payload = {
        name: formData.name,
        centre: formData.centre,
        capacity: Number(formData.capacity),
      };

      const res = await axios.patch(UPDATE_KALANJAY_BATCH(batchId), payload, {
        headers,
      });

      // Reload Batches
      setPage(0);
      fetchBatches(rowsPerPage, 0, search, centreTab);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const deleteBatch = async (batchId) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      await axios.delete(DELETE_KALANJAY_BATCH(batchId), { headers });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveBatch = async (form) => {
    if (!validate(form)) return;
    try {
      if (editData) {
        await updateBatch(editData.id, form);
      } else {
        await addBatch(form);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const centreTabs = centres.map((c) => ({
    label: `${c.name} (${c.batches_count ?? 0})`,
    value: c.id,
  }));

  const columns = [
    {
      id: "name",
      label: "Batch Name",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: row.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              ...typography.bodyLarge,
              color: ColorsConstants.primaryPurple100,
              cursor: "pointer",
              textDecoration: "underline",
              textDecorationColor: ColorsConstants.primaryPurple100 + "60",
            }}
            onClick={() => onSelectBatch?.(row)}
          >
            {row.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: "centre",
      label: "Centre",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
          {row.centre_name}
        </Typography>
      ),
    },
    {
      id: "capacity",
      label: "Capacity",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          {row.enrolled_count}
        </Typography>
      ),
    },
    {
      id: "collection",
      label: "Collection",
      render: (row) => {
        return (
          <Typography
            sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#008026" }}
          >
            ₹{Number(row.collection).toLocaleString("en-IN")}
          </Typography>
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <ActionBtn
            icon={VisibilityOutlinedIcon}
            color={ColorsConstants.primaryPurple100}
            onClick={() => onSelectBatch?.(row)}
            title="View batch"
          />
          <ActionBtn
            icon={EditOutlinedIcon}
            color={
              hasEditPermission
                ? ColorsConstants.primaryGray60
                : ColorsConstants.primaryGray20
            }
            onClick={() => {
              if (hasEditPermission) {
                setEditData(row);
                setDialogOpen(true);
              }
            }}
            title="Edit batch"
          />
          {/* <ActionBtn
            icon={DeleteOutlineIcon}
            color={ColorsConstants.error}
            onClick={() => deleteBatch(row.id)}
            title="Delete batch"
          /> */}
        </Box>
      ),
    },
  ];

  if (!hasViewPermission) {
    return (
      <Stack sx={{ mt: 16 }}>
        <NoPermission />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography sx={{ ...typography.h1, mb: 3, fontSize: {  xs: 22, md: typography.h1.fontSize,  }, }}>Batches</Typography>
      <Box
        
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            md: "row",
          },
            alignItems: {
              xs: "stretch",
              md: "center",
            },
              justifyContent: "space-between",
              gap: 2,
              }}
              >
        <CustomTabs
          value={centreTab}
          onChange={(_, v) => {
            setCentreTab(v);
            setSearch("");
            onCentreChange(v);
          }}
          tabs={centreTabs}
          sx={{ width: "100%", maxWidth: "100%" }}
        />
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 1.5, width: { xs: "100%", md: "auto" } }}>
          <TextField
            size="small"
            placeholder="Search batch name…"
            value={search} // ← controlled from parent
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: grayFontColor, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...fieldSx, width: { xs: "100%", sm: 250, md: 220 } }}
          />
          <Typography
            sx={{
              ...typography.bodySmall,
              color: grayFontColor,
              whiteSpace:{
                xs:"normal",
                sm:"nowrap"
              },
              textAlign:{
                xs:"center",
                sm:"left"
              }
            }}
          >
            {totalCount} batches
          </Typography>
          <ButtonOne  sx={{ width: { xs: "100%", sm: "auto" } }}
            startIcon={<AddIcon />}
            onClick={() => {
              setEditData(null);
              setDialogOpen(true);
            }}
            disabled={!hasCreatePermission}
          >
            Add Batch
          </ButtonOne>
        </Box>
      </Box>

      <AppTable
        columns={columns}
        rows={batches} // ← no more filtered, API handles it
        rowKey="id"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        emptyMessage={
          search ? "No batches match your search." : "No batches yet."
        }
      />
      <BatchFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditData(null);
        }}
        onSave={handleSaveBatch}
        editData={editData}
        centres={centres}
        addBatch={addBatch}
      />
    </Box>
  );
};

export default KalanjayBatches;
